import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import SubscriptionStatusCard from "@/components/SubscriptionStatusCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import ProfileCardMenu from "@/components/ProfileCardMenu";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, Heart, Handshake, MapPin, MessageCircle, Lock, Search,
  ShieldCheck, Sparkles, ArrowRight,
} from "lucide-react";

const MODES = [
  {
    id: "FIND_FRIEND",
    title: "Find a Friend",
    tagline: "Make new friends in your city",
    desc: "Browse people open to friendship and companionship — chai meets, run buddies, movie nights.",
    cta: "Browse friends",
    Icon: Users,
    gradient: "from-teal-500 to-emerald-500",
    accent: "text-teal-700 bg-teal-50 border-teal-200",
  },
  {
    id: "DATE_FRIEND",
    title: "Date a Friend",
    tagline: "Meet people open to dating",
    desc: "Thoughtful profiles of people in your pincode who are looking to date — no swipe fatigue.",
    cta: "Browse dates",
    Icon: Heart,
    gradient: "from-rose-500 to-pink-500",
    accent: "text-rose-700 bg-rose-50 border-rose-200",
  },
  {
    id: "RENT_FRIEND",
    title: "Rent a Friend",
    tagline: "Paid companionship, trusted & verified",
    desc: "Hire a friend for an event, a city tour, a plus-one — admin-verified profiles only.",
    cta: "Browse services",
    Icon: Handshake,
    gradient: "from-amber-500 to-orange-500",
    accent: "text-amber-800 bg-amber-50 border-amber-200",
  },
];

function initials(name) {
  return (name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}

function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
}

function ProfileCard({ u, subscribed, modeId, onConnect, onBlocked }) {
  const age = ageFromDob(u.dob);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const pic = u.picture && u.picture.startsWith("/api/")
    ? `${BACKEND}${u.picture}`
    : u.picture;
  return (
    <div data-testid={`friend-card-${u.user_id}`} className="relative bg-white rounded-2xl border border-slate-200 p-5 hover-lift overflow-hidden">
      <div className="absolute top-3 right-3 z-10">
        <ProfileCardMenu targetUserId={u.user_id} targetName={u.name} onBlocked={onBlocked} />
      </div>
      <div className={subscribed ? "" : "locked-blur"}>
        <div className="flex items-start gap-4 pr-10">
          {pic ? (
            <img src={pic} alt="" className="w-14 h-14 rounded-full object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-500 to-amber-400 text-white flex items-center justify-center font-bold text-lg">
              {initials(u.name)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg text-slate-900 truncate flex items-center gap-2">
              <span className="truncate">{u.name}</span>
              {age ? <span className="text-sm text-slate-500 font-normal">· {age}</span> : null}
              <VerifiedBadge user={u} />
            </div>
            <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5">
              <MapPin className="w-3.5 h-3.5" /> {u.area ? `${u.area}, ` : ""}{u.city || "—"}
            </div>
            {u.pincode && <span className="inline-block mt-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">PIN {u.pincode}</span>}
          </div>
        </div>
        {u.bio && <p className="mt-3 text-sm text-slate-600 line-clamp-2">{u.bio}</p>}
      </div>
      {subscribed ? (
        <Button data-testid={`connect-${u.user_id}`} onClick={() => onConnect(u.user_id)} className="mt-4 w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full">
          <MessageCircle className="w-4 h-4 mr-2" /> {modeId === "RENT_FRIEND" ? "Request / Book" : "Connect"}
        </Button>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
          <div className="text-center text-white px-6">
            <Lock className="w-7 h-7 mx-auto mb-2" />
            <p className="font-semibold">Unlock profile</p>
            <Button onClick={() => (window.location.href = "/plans")} className="mt-3 bg-amber-500 hover:bg-amber-600 rounded-full">Upgrade</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeMode = params.get("mode") || "";
  const [users, setUsers] = useState([]);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [subStatus, setSubStatus] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (activeMode) p.service_mode = activeMode;
      if (query) {
        // Treat 6-digit as pincode, otherwise city search
        if (/^\d{6}$/.test(query.trim())) p.pincode = query.trim();
        else p.city = query.trim();
      }
      const r = await api.get("/users", { params: p });
      setUsers(r.data.users || []);
      setSubscribed(r.data.subscribed);
    } finally {
      setLoading(false);
    }
  }, [activeMode, query]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { api.get("/subscription/status").then((r) => setSubStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user && !user.profile_completed) navigate("/profile");
  }, [user, navigate]);

  const setMode = (id) => {
    const p = new URLSearchParams(params);
    if (id) p.set("mode", id); else p.delete("mode");
    setParams(p);
  };

  const activeModeObj = MODES.find((m) => m.id === activeMode);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Hero */}
        <section className="rounded-3xl bg-gradient-to-br from-slate-900 via-teal-900 to-emerald-900 text-white p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-amber-300/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl"></div>
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
              <Sparkles className="w-3 h-3" /> Welcome back {user?.name?.split(" ")[0] || "friend"}
            </div>
            <h1 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-2xl">
              Find the right friend for you.
            </h1>
            <p className="mt-3 text-white/80 max-w-xl">
              Connect with people, make new friends and discover meaningful experiences.
            </p>
            <div className="mt-6 flex items-center gap-2 max-w-lg bg-white rounded-full p-1.5 shadow-xl">
              <Search className="w-5 h-5 text-slate-400 ml-3" />
              <Input
                data-testid="hero-search-input"
                placeholder="Search by city, name or 6-digit pincode"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchUsers()}
                className="border-0 focus-visible:ring-0 text-slate-800 placeholder:text-slate-400"
              />
              <Button data-testid="hero-search-btn" onClick={fetchUsers} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-5">Search</Button>
            </div>
          </div>
        </section>

        {/* 3 Service cards */}
        <section>
          <h2 className="font-heading text-2xl font-bold text-slate-900">What are you here for?</h2>
          <p className="text-slate-500 mt-1 text-sm">Pick a mode to filter profiles. You can switch anytime.</p>
          <div className="mt-5 grid md:grid-cols-3 gap-5" data-testid="service-cards">
            {MODES.map((m) => {
              const active = activeMode === m.id;
              return (
                <button
                  key={m.id}
                  data-testid={`service-card-${m.id}`}
                  onClick={() => setMode(active ? "" : m.id)}
                  className={`relative text-left rounded-3xl bg-white border-2 p-6 transition hover:shadow-xl group ${active ? "border-teal-500 ring-4 ring-teal-100" : "border-slate-200"}`}
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${m.gradient} text-white flex items-center justify-center shadow-md`}>
                    <m.Icon className="w-7 h-7" />
                  </div>
                  <div className="mt-5 font-heading text-2xl font-extrabold text-slate-900">{m.title}</div>
                  <div className={`inline-flex items-center gap-1 mt-1 text-[11px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${m.accent}`}>{m.tagline}</div>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{m.desc}</p>
                  <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-teal-700 group-hover:gap-2 transition-all">
                    {m.cta} <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <SubscriptionStatusCard status={subStatus} />

        {/* Results */}
        <section>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              {activeModeObj ? `${activeModeObj.title} · ${users.length} profiles` : `All profiles · ${users.length}`}
            </h2>
            {activeMode && (
              <button onClick={() => setMode("")} className="text-sm font-semibold text-slate-500 hover:text-slate-700">Clear filter</button>
            )}
          </div>

          {loading ? (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-56 rounded-2xl bg-slate-100 animate-pulse"></div>)}
            </div>
          ) : users.length === 0 ? (
            <div className="mt-5 text-center py-16 bg-white rounded-2xl border border-slate-200">
              <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No profiles found with these filters.</p>
              <p className="text-sm text-slate-400 mt-1">Try a different mode or search term.</p>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="users-grid">
              {users.slice(0, 24).map((u) => (
                <ProfileCard
                  key={u.user_id}
                  u={u}
                  subscribed={subscribed}
                  modeId={activeMode}
                  onConnect={(id) => navigate(`/chat/${id}`)}
                  onBlocked={(id) => setUsers((prev) => prev.filter((x) => x.user_id !== id))}
                />
              ))}
            </div>
          )}
        </section>

        {/* Safety banner */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-teal-600 shrink-0" />
          <div>
            <div className="font-heading font-bold text-slate-900">Your safety, our priority</div>
            <p className="text-sm text-slate-600 mt-0.5">
              Admin verifies every payment. Report suspicious profiles via the three-dot menu — we review within 24h. We never share your contact without an active subscription.
              <Link to="/faq" className="font-semibold text-teal-700 ml-1">Learn more</Link>
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
